/**
 * Hit-effect sprites and their square particles (W1-06; R§5.1-R§5.3, S§4, A§7.5).
 *
 * Sprite of a burst emitted at `e` (age = t - e, u = age / hitFxDuration, drawn while 0 <= u < 1):
 *   frame    = min(N - 1, floor(u * N)), N = cols * rows (row-major atlas)
 *   alphaMul = u < 0.5 ? 1 - 0.6 u : 1.4 (1 - u)          (1 -> 0.7 at the middle -> 0)
 *   half     = hitFxScale * noteScale / 5                 (square, world units)
 *   position = lineMatrix(e) * (x, yOff / ar), in UI space (x, -y); yOffset is NOT mirrored for below notes
 *   rotation = hitFxRotate ? (ticks: full line rotation incl. parents; others: the line's own rotation)
 *              + (above ? 0 : 180) : 0, given to the renderer as -world angle (y-down canvas)
 * Colours: fxPerfect = colorPerfect when hitFxTinted, else white (fxGood likewise). Autoplay hits and hold ends
 * use fxPerfect and ignore tintHitEffects (Phira's autoplay; `strictAutoplayHitColor` off honours the tint like
 * real play); hold ticks use tintHitEffects ?? fxPerfect. Judged play (JudgeView): Perfect/Good hits burst at the
 * judgement time with tintHitEffects ?? fxPerfect/fxGood, Bad/Miss nothing; a hit hold ticks from its judgement
 * while it is held, in the colour of its head grade, and the ticks emitted before a release keep fading out
 * (holdTicks.ts reconstructs the release from the frames, change request CR-W1-06-6). Nothing at all when the
 * Phira player setting `particles` is off. Preview filters (hidden, solo) apply; focus-hitsounds does not (it only
 * filters sounds).
 */

import { Search } from '../../math/search';
import type { LineId, NoteId } from '../../model/types';
import type { RGB, RGBAf } from '../../util/types';
import { lineTransformOf } from '../transformCache';
import type { EngineCompileContext, FrameDrawListBuilder, FrameOptions, JudgeGrade } from '../types';
import { FRAME_DT, HIT_FX_HALF_DIVISOR, HOLD_TICK_INTERVAL, LIMIT_BAD, WHITE } from './constants';
import { emissionCache, emissionPasses, forEachEmission, type NoteEmission } from './emissions';
import { holdTickTracker, makeHoldTickWindow } from './holdTicks';
import { burstSeed, pushParticles } from './particles';
import { globalOf, lineRt, realNoteIndex } from './store';

const fr = Math.fround;

/** Alpha multiplier of the sprite and particles at progress u in [0, 1). */
export function hitFxAlpha(u: number): number {
  return u < 0.5 ? 1 - 0.6 * u : 1.4 * (1 - u);
}

/** Atlas frame at progress u for a grid of `frames` frames. */
export function hitFxFrame(u: number, frames: number): number {
  return Math.max(0, Math.min(frames - 1, Math.floor(u * frames)));
}

function rgbToRgbaf(c: RGB): RGBAf {
  return [c[0] / 255, c[1] / 255, c[2] / 255, 1];
}

/** One burst to draw. */
interface Burst {
  line: LineId;
  time: number;
  x: number;
  yOff: number;
  above: boolean;
  /** Ticks rotate with the full line rotation (parents included); hits and ends with the line's own rotation. */
  fullRotation: boolean;
  color: RGBAf;
  note: number;
  seq: number;
}

const burst: Burst = {
  line: 0 as LineId,
  time: 0,
  x: 0,
  yOff: 0,
  above: true,
  fullRotation: false,
  color: WHITE,
  note: 0,
  seq: 0,
};

function drawBurst(
  ctx: EngineCompileContext,
  b: Burst,
  t: number,
  opts: FrameOptions,
  ar: number,
  out: FrameDrawListBuilder,
): void {
  const skin = opts.skin;
  const age = t - b.time;
  const u = age / skin.hitFxDuration;
  if (!(u >= 0 && u < 1)) return;
  // The line transform at the emission time: the same for every frame of the burst (cached by the engine).
  const s = lineTransformOf(ctx, b.line, b.time);
  const yy = fr(b.yOff / ar);
  const [m0, m1, m2, m3, m4, m5] = s.m;
  const wx = m0 * b.x + m2 * yy + m4;
  const wy = m1 * b.x + m3 * yy + m5;
  const worldRot = skin.hitFxRotate ? (b.fullRotation ? s.rot : s.ownRot) + (b.above ? 0 : 180) : 0;
  const am = hitFxAlpha(u);
  const color: RGBAf = [b.color[0], b.color[1], b.color[2], b.color[3] * am];
  const noteScale = opts.player.noteScale;
  const [cols, rows] = skin.hitFx;
  const x = wx;
  const y = -wy;
  out.hitFx.push({
    x,
    y,
    rotDeg: worldRot === 0 ? 0 : -worldRot,
    half: (skin.hitFxScale * noteScale) / HIT_FX_HALF_DIVISOR,
    frame: hitFxFrame(u, cols * rows),
    color,
  });
  if (!skin.hideParticles) {
    pushParticles(burstSeed(b.note, b.seq), x, y, age, skin.hitFxScale, noteScale, color, out.particles);
  }
}

/** Fills `out.hitFx` and `out.particles` (replacing their content). */
export function pushHitFx(ctx: EngineCompileContext, t: number, opts: FrameOptions, out: FrameDrawListBuilder): void {
  out.hitFx.length = 0;
  out.particles.length = 0;
  if (!opts.player.particles) return;
  const ar = out.viewport.ar;
  const skin = opts.skin;
  const fxPerfect = skin.hitFxTinted ? skin.colorPerfect : WHITE;
  const fxGood = skin.hitFxTinted ? skin.colorGood : WHITE;
  const dur = skin.hitFxDuration;
  if (!(dur > 0)) return;
  if (opts.judge === null) {
    forEachEmission(ctx, t - dur, t, opts.rate, true, (e: NoteEmission) => {
      if (!e.fx || !emissionPasses(opts.filters, e.line, false)) return;
      burst.line = e.line;
      burst.time = e.time;
      burst.x = e.x;
      burst.yOff = e.yOff;
      burst.above = e.above;
      burst.fullRotation = e.kind === 'holdTick';
      const tint = e.fxTint === null ? null : rgbToRgbaf(e.fxTint);
      burst.color = e.kind === 'holdTick' || !opts.player.strictAutoplayHitColor ? (tint ?? fxPerfect) : fxPerfect;
      burst.note = e.note;
      burst.seq = e.seq;
      drawBurst(ctx, burst, t, opts, ar, out);
    });
    return;
  }
  judgedBursts(ctx, t, opts, ar, fxPerfect, fxGood, out);
}

function gradeColor(grade: JudgeGrade, tint: RGB | null, fxPerfect: RGBAf, fxGood: RGBAf): RGBAf | null {
  if (grade !== 'perfect' && grade !== 'good') return null;
  if (tint !== null) return rgbToRgbaf(tint);
  return grade === 'perfect' ? fxPerfect : fxGood;
}

const tickWin = makeHoldTickWindow();

/** Judged play: bursts of Perfect/Good hits and the ticks of hit holds. */
function judgedBursts(
  ctx: EngineCompileContext,
  t: number,
  opts: FrameOptions,
  ar: number,
  fxPerfect: RGBAf,
  fxGood: RGBAf,
  out: FrameDrawListBuilder,
): void {
  const judge = opts.judge;
  if (judge === null) return;
  const dur = opts.skin.hitFxDuration;
  const idx = realNoteIndex(ctx);
  const g = globalOf(ctx);
  // Non-hold hits: a judgement lies within the bad window of the note time.
  let k = Search.lowerBound(idx.time, t - dur - LIMIT_BAD);
  for (; k < idx.n; k++) {
    if ((idx.time[k] ?? 0) > t + LIMIT_BAD) break;
    const id = idx.id[k] ?? -1;
    const line = g.noteLine.get(id as NoteId);
    if (line === undefined) continue;
    const rt = lineRt(ctx, line);
    const i = rt.indexOf.get(id as NoteId);
    if (i === undefined || rt.isHold(i)) continue;
    const st = judge.status(rt.noteId(i));
    if (st === null || st.at > t || t - st.at >= dur) continue;
    const color = gradeColor(st.grade, rt.fxTint[i] ?? null, fxPerfect, fxGood);
    if (color === null || !emissionPasses(opts.filters, line, false)) continue;
    burst.line = line;
    burst.time = st.at;
    burst.x = rt.x[i] ?? 0;
    burst.yOff = rt.yOff[i] ?? 0;
    burst.above = rt.above[i] === 1;
    burst.fullRotation = false;
    burst.color = color;
    burst.note = id;
    burst.seq = 0;
    drawBurst(ctx, burst, t, opts, ar, out);
  }
  // Hold ticks from the head judgement while the hold is held (holdTicks.ts: release seen across frames).
  const rate = opts.rate > 0 && Number.isFinite(opts.rate) ? opts.rate : 1;
  const interval = HOLD_TICK_INTERVAL / rate;
  const tracker = holdTickTracker(ctx, judge);
  for (const { rt, i } of emissionCache(ctx).holds) {
    const id = rt.noteId(i);
    const end = rt.endTime[i] ?? 0;
    // Observed even when filtered out, so the record stays current while a line is hidden.
    if (!tracker.window(id, judge.status(id), t, end, tickWin)) continue;
    const color = gradeColor(tickWin.grade, rt.fxTint[i] ?? null, fxPerfect, fxGood);
    if (color === null || !emissionPasses(opts.filters, rt.line, false)) continue;
    const first = tickWin.at + FRAME_DT;
    const until = tickWin.until;
    const inclusive = tickWin.inclusive;
    let kk = Math.max(0, Math.ceil((t - dur - first) / interval));
    for (; ; kk++) {
      const tt = first + kk * interval;
      if (tt > t || !(tt < end) || (inclusive ? tt > until : !(tt < until))) break;
      if (t - tt >= dur) continue;
      burst.line = rt.line;
      burst.time = tt;
      burst.x = rt.x[i] ?? 0;
      burst.yOff = rt.yOff[i] ?? 0;
      burst.above = rt.above[i] === 1;
      burst.fullRotation = true;
      burst.color = color;
      burst.note = rt.id[i] ?? -1;
      burst.seq = 2 + kk;
      drawBurst(ctx, burst, t, opts, ar, out);
    }
  }
}
